import { ChangeDetectorRef, Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core'
import { AuthService } from '../../services/auth.service'
import { ActivatedRoute, Router } from '@angular/router'
import { Message } from 'primeng/api'
import { AppConfigService } from '../../../app-config/app-config.service'
import { AuthFacadeService } from '../../services/auth-facade.service'
import { JmlLibConfig } from '../../../jml-lib-config'
import { JML_LIB_CONFIG } from '../../../jml-lib-config.token'
import { NotificationService } from '../../../services/notification.service'
import { ImageService } from '../../../services/image.service'
import { catchError, take } from 'rxjs/operators'
import { ConfigApiService } from '../../../services/config-api.service'
import { JmlTitleService } from '../../../services/jml-title.service'
import { CONSTANTS } from '../../../../../../shared/constants'
import { NOTIFICATION_TOAST_VISIBILITY } from '../../../services/notification.constants'

@Component({
  selector: 'jml-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit {
  uiVersion: string
  serverVersion: string

  msgs: Message[] = []
  loginFailed = false

  loginDisabled = false

  uploadLicenseMessage = ''

  oldpass: string
  newpass: string
  repeatpass: string

  // base64 encoded app-logo
  appImgSrc: string | ArrayBuffer
  appImgError = false

  welcomeText: string = 'Willkommen bei JMeleon!'

  @ViewChild('username') usernameRef: ElementRef
  @ViewChild('password') passwordRef: ElementRef

  constructor(
    public authService: AuthService,
    public router: Router,
    public appConfig: AppConfigService,
    private authFacade: AuthFacadeService,
    @Inject(JML_LIB_CONFIG) private jmlLibConfig: JmlLibConfig,
    private notify: NotificationService,
    private imgService: ImageService,
    private configApiService: ConfigApiService,
    private jmlTitleService: JmlTitleService,
    private route: ActivatedRoute,
    private cd: ChangeDetectorRef
  ) {
    this.route.params.subscribe((params) => {
      var param = decodeURIComponent(params.uploadLicenseMessage)
      // this is a special case (a workaround for a routing problem (redirect to 'login/' with query param))
      // and not meant as a uploadLicenseMessage
      if (param === 'set') {
        // setting loginName..
        return
      }
      this.uploadLicenseMessage = param
    })
  }

  ngAfterViewInit() {
    if (this.uploadLicenseMessage) {
      console.log('uploadLicenseMessage: ', this.uploadLicenseMessage)
      // Need to do this here since notifications component is not initialized in OnInit
      this.notify.addSuccessNotification(this.uploadLicenseMessage, null, NOTIFICATION_TOAST_VISIBILITY.STICKY)
      this.cd.detectChanges() // Need to detect changes since toast was added
    }
    this.route.queryParams.pipe(take(1)).subscribe((params) => {
      var loginNameParam = params['loginName']
      if (!!loginNameParam) {
        var loginName = decodeURIComponent(loginNameParam)
        this.usernameRef.nativeElement.value = loginName
        this.passwordRef.nativeElement.focus()
      }
    })
  }

  ngOnInit() {
    this.jmlTitleService.setAppTitle('Login')
    this.appConfig
      .getUIInfo$()
      .pipe(take(1))
      .subscribe((uiInfo) => {
        this.uiVersion = uiInfo.version + '.' + uiInfo.build_no + '.' + uiInfo.git_sha
        // Story #1733: https://redmine.simply4it.de/issues/1733
        this.productiveCheck(uiInfo.FEBEsameURLcheck, window.location.href, uiInfo.baseUrl)
      })
    this.appConfig
      .getServerInfo$()
      .pipe(take(1))
      .subscribe((serverInfo) => {
        this.serverVersion = serverInfo.version
      })

    this.authFacade.userProfileError$.subscribe((errMsg) => {
      // console.log('err msg: ', errMsg);
      if (!errMsg) {
        // console.log('empty err msg.');
        return
      }
      this.msgs = []
      this.msgs.push({ severity: 'error', summary: '', detail: errMsg })
    })

    this.imgService
      .getAppLogo$()
      .pipe(
        take(1),
        catchError((err) => {
          console.log('error loading application logo:', err)
          this.appImgError = true
          return <string>null
        })
      )
      .subscribe((img) => {
        this.appImgSrc = img
      })

    this.configApiService.getWelcomeText$().subscribe((text) => (this.welcomeText = text))
  }

  // Story #1733: https://redmine.simply4it.de/issues/1733
  productiveCheck(executeCheck: boolean, frontendURL: string, backendURL: string) {
    if (!executeCheck) {
      console.log('No productive environment. Compatibility of frontend and backend URL has not been checked.')
      return
    }

    // get the URL part until the first slash after http://
    const frontendCompString = frontendURL.slice(0, frontendURL.indexOf('/', frontendURL.search('://') + 3))
    const backendCompString = backendURL.slice(0, backendURL.indexOf('/', backendURL.search('://') + 3))

    if (frontendCompString !== backendCompString) {
      console.error(
        'frontend URL: ' +
          frontendCompString +
          '\n' +
          'backend URL:  ' +
          backendCompString +
          '\n' +
          'Frontend and backend URL mismatch!  Login disabled.'
      )
      this.msgs = []
      this.msgs.push({
        closable: true,
        severity: 'error',
        summary: '',
        detail:
          '<b>Frontend and backend URL mismatch! <br>Login disabled.</b><br>' +
          'frontend URL: ' +
          frontendCompString +
          '<br>' +
          'backend URL:  ' +
          backendCompString +
          '<br>'
      })
      this.loginDisabled = true
    } else {
      console.log(
        'frontend URL: ' +
          frontendCompString +
          '\n' +
          'backend URL:  ' +
          backendCompString +
          '\n' +
          'Success! Frontend and backend URL compatible!'
      )
    }
  }

  login(userName: string, password: string) {
    this.authFacade.login(userName, password)
  }

  changePassword() {
    if (this.newpass !== this.repeatpass) {
      this.msgs = []
      this.notify.addErrorNotification('Die beiden Passwörter stimmen nicht überein.')
      return
    }
    this.authFacade.changePassword(this.oldpass, this.newpass)
  }

  logout(): void {
    this.authFacade.logout()
  }
}
